import React, { useState, useEffect, useCallback } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { useApp } from "../context/AppContext";
import { CheckCircle2, XCircle, Coffee } from "lucide-react";

export function ManagerLunchScreen() {
  const { fetchTodayLunchRequests, updateLunchRequest } = useApp();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);
  
  const loadRequests = useCallback(async () => {
    try {
      const res = await fetchTodayLunchRequests();
      setRequests(res.requests || []);
    } catch (err) {
      console.error("Error fetching lunch requests:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchTodayLunchRequests]);

  useEffect(() => {
    loadRequests();
    const timer = setInterval(loadRequests, 4000); // Sync every 4s
    return () => clearInterval(timer);
  }, [loadRequests]);

  const handleStatusChange = async (id, status) => {
    setActioningId(id);
    try {
      await updateLunchRequest(id, status);
      await loadRequests();
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    } finally {
      setActioningId(null);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, textAlign: "center", color: TOKENS.muted, fontSize: 14 }}>Loading lunch requests...</div>;
  }

  const totalApproved = requests.filter(r => r.status === 'approved').length;
  const totalPending = requests.filter(r => r.status === 'requested').length;
  const totalRejected = requests.filter(r => r.status === 'rejected').length;

  return (
    <div>
      <SectionLabel icon={<Coffee size={16} />} text="Today's Lunch Requests" />
      
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Card style={{ flex: 1, padding: 16, background: TOKENS.successBg, border: `1px solid ${TOKENS.success}40` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.success }}>Approved</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: TOKENS.success }}>{totalApproved}</div>
        </Card>
        <Card style={{ flex: 1, padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.muted }}>Pending</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: TOKENS.navyDeep }}>{totalPending}</div>
        </Card>
        <Card style={{ flex: 1, padding: 16, background: TOKENS.dangerBg, border: `1px solid ${TOKENS.danger}40` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.danger }}>Rejected</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: TOKENS.danger }}>{totalRejected}</div>
        </Card>
      </div>

      {requests.length === 0 ? (
        <div style={{ textAlign: "center", padding: 32, background: "#fff", borderRadius: 16, border: `1px dashed ${TOKENS.border}` }}>
          <div style={{ color: TOKENS.muted, fontSize: 14, fontWeight: 500 }}>No lunch requests for today.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {requests.map(req => {
            const isLoading = actioningId === req.id;
            return (
              <Card key={req.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 2 }}>
                      {req.name || req.employee_id}
                    </div>
                    <div style={{ fontSize: 12, color: TOKENS.muted, marginBottom: 8 }}>
                      Emp ID: {req.employee_id}
                    </div>
                    <div style={{ fontSize: 12, color: TOKENS.ink, display: "flex", gap: 12 }}>
                      <span>Requested At: {req.requested_at ? req.requested_at.slice(11, 16) : ""}</span>
                    </div>
                  </div>
                  <StatusPill 
                    status={req.status === 'requested' ? 'pending' : req.status} 
                    text={req.status.charAt(0).toUpperCase() + req.status.slice(1)} 
                  />
                </div>
                
                {req.status === "requested" && (
                  <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${TOKENS.border}` }}>
                    <button
                      onClick={() => handleStatusChange(req.id, "approved")}
                      disabled={isLoading}
                      style={{
                        flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                        background: TOKENS.successBg, color: TOKENS.success,
                        fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                        cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.6 : 1
                      }}
                    >
                      <CheckCircle2 size={16} /> Approve
                    </button>
                    <button
                      onClick={() => handleStatusChange(req.id, "rejected")}
                      disabled={isLoading}
                      style={{
                        flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                        background: TOKENS.dangerBg, color: TOKENS.danger,
                        fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                        cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.6 : 1
                      }}
                    >
                      <XCircle size={16} /> Reject
                    </button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
